/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { casesStore, useCasesActions } from '@/features/cases/case/stores/cases.store';
import { useNotificationActions } from '@/features/notifications/stores/notification.store';
import { useCaseNotificationActionsStore } from '@/features/cases/case/stores/case-notification.store';
import { caseMessageCountStore } from '@/features/cases/case/stores/case-message-count.store';

import { TCase } from '@/features/cases/case/api/types/case.type';
import { soundOne, soundTwo } from '@/assets/sound';
import { queryClient } from '@/lib/react-query';
import {
  DEFAULT_INFINITE_GROUP_MESSAGES_LIMIT_PARAMS,
  GET_INFINITE_GROUP_MESSAGES_QUERY_KEY,
} from '@/features/cases/case/api/get-group-messages.api';
import { IUseSocketReturn } from '@/websockets/hooks/use-socket';
import { TSatisfyClientToServerEvents } from '@/websockets/contexts/socket.context';
import useAuthContext from '@/features/auth/_shared/contexts/auth.context';
import useSound from 'use-sound';

import {
  DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS,
  GET_INFINITE_MESSAGES_QUERY_KEY,
} from '../../case/api/get-messages.api';
import { notify } from '@/utils/notify';
import {
  useIsOnActiveCaseSoundPage,
  useIsOnUnassignedCaseSoundPage,
} from '@/features/notifications/stores/new-notification.store';
import { updateOrSaveCaseUnread } from '@/features/cases/case/utils/case-unread';

export type TMessageId = string;

export type TCaseListPayload = {
  activeCases: TCase[];
  resolvedCases: TCase[];
  unassignedCases: TCase[];
};

export type TCaseServerToClientEvents = {
  case_list: (payload: TCaseListPayload) => void;
  new_message: (payload: TCase) => void;
  message_sent_status: (payload: TMessageId) => void;
};

export function useCaseSocketListeners(
  socket: IUseSocketReturn<TCaseServerToClientEvents, TSatisfyClientToServerEvents>
) {
  // HOOKS
  const {
    state: { user },
  } = useAuthContext();
  const userTeamIds = useMemo(() => {
    return user?.teams?.map((team) => team.teamId) ?? [];
  }, [user?.teams]);
  const eventListenersAttached = useRef(false); // Prevent double-attachment in Strict Mode
  const isOnActiveCaseSoundPage = useIsOnActiveCaseSoundPage();
  const isOnUnassignedCaseSoundPage = useIsOnUnassignedCaseSoundPage();

  const { setIsHasNewActiveCaseNotification, setIsHasNewUnassignedCaseNotification } = useNotificationActions();
  const { addCaseNotification } = useCaseNotificationActionsStore();

  const [playSoundOne] = useSound(soundOne, {
    volume: 0.5,
    interrupt: true,
  });

  const [playSoundTwo] = useSound(soundTwo, {
    volume: 0.5,
    interrupt: true,
  });

  const { setListActiveCase, setListUnassignCase, setListResolvedCase, setSelectedCase, setIsLoadingFromWebsocket } =
    useCasesActions();

  // HELPERS
  const isExecuteNotification = useMemo(() => {
    return user?.role?.name === 'admin' || user?.role?.name === 'supervisor' || user?.role?.name === 'agent';
  }, [user]);

  const handleCaseListUpdate = useCallback(
    (payload: TCaseListPayload) => {
      // setIsLoadingFromWebsocket(true);

      try {
        const selectedCase = casesStore.getState().selectedCase;
        const chosenCaseId = selectedCase?.caseId;

        const isExistInActive = payload.activeCases.some((item) => item.caseId === chosenCaseId);
        const isExistInUnassigned = payload.unassignedCases.some((item) => item.caseId === chosenCaseId);

        const isChoosedCaseActive = selectedCase?.userId && selectedCase.conversationStatus === 'open';
        const isChoosedCaseResolved = selectedCase?.userId && selectedCase.conversationStatus === 'closed';

        if (isChoosedCaseActive && !isExistInActive) {
          setSelectedCase(null);
        }
        if (isChoosedCaseResolved && !isExistInUnassigned) {
          setSelectedCase(null);
        }

        setListActiveCase(payload.activeCases);
        setListUnassignCase(payload.unassignedCases);
      } finally {
        setIsLoadingFromWebsocket(false);
      }
    },
    [setListActiveCase, setListUnassignCase, setSelectedCase, setIsLoadingFromWebsocket]
  );

  const updateGroupMessagesCache = useCallback((groupInfoId: string, messageId: string, payload: TCase) => {
    queryClient.setQueryData(
      [GET_INFINITE_GROUP_MESSAGES_QUERY_KEY, groupInfoId, DEFAULT_INFINITE_GROUP_MESSAGES_LIMIT_PARAMS],
      (old: any) => {
        if (!old?.pages?.[0]?.payload) return old;

        const firstPage = old.pages[0];
        const messageExists = firstPage.payload.some((msg: any) => msg.messageId === messageId);
        if (messageExists) return old;

        return {
          ...old,
          pages: [{ ...firstPage, payload: [...firstPage.payload, payload] }, ...old.pages.slice(1)],
        };
      }
    );
  }, []);

  const handleNewMessage = useCallback(
    async (payload: TCase) => {
      try {
        const { caseId, senderType, customerName, content, channelName, userId, messageId } = payload;
        const isUnassignedCase = !userId;
        const isActiveCase = userId;
        const isResolvedCase = payload?.conversationStatus ? Boolean(payload?.conversationStatus === 'closed') : false;
        const customerSender = payload?.senderType ? Boolean(payload?.senderType === 'customer') : false;
        const isHasTeam = payload?.teamId ? Boolean(payload?.teamId) : false;
        const isHasGroup = payload?.groupInfoId ? Boolean(payload?.groupInfoId) : false;
        const groupName = isHasGroup ? (payload?.groupName ?? '-') : undefined;
        const isGroup = payload?.isGroup ? Boolean(payload?.isGroup) : false;
        const isTeamCase =
          (isActiveCase &&
            user?.role?.name === 'supervisor' &&
            payload?.teamId &&
            userTeamIds.includes(payload?.teamId) &&
            payload?.userId !== user?.userId) ||
          false;
        const isAllActiveCase = isActiveCase && user?.role?.name === 'admin';
        const redirectUrl = isUnassignedCase
          ? `/unassigned-cases?tabCase=allCase&case=${caseId}`
          : isTeamCase
            ? `/active-cases?tabCase=teamCase&case=${caseId}`
            : isAllActiveCase
              ? `/active-cases?tabCase=allCase&case=${caseId}`
              : `/active-cases?tabCase=myCase&case=${caseId}`;

        if (customerSender && isExecuteNotification) {
          if (isUnassignedCase) {
            // if (enabledUnsignedCaseSound) {
            //   if (!isUserOnUnassignedCasePage) {
            //     playSoundTwo();
            //   }
            // }

            // force to active if the case has a team
            if (isHasTeam) {
              if (isOnActiveCaseSoundPage) {
                playSoundOne();
              }
              setIsHasNewActiveCaseNotification(true);
              addCaseNotification({
                caseId,
                isUnsigned: false,
                isActive: true,
                isRead: false,
                userId: user?.userId,
              });
            } else {
              if (isOnUnassignedCaseSoundPage) {
                playSoundTwo();
              }
              setIsHasNewUnassignedCaseNotification(true);
              // force to unassigned if the case does not have a team
              addCaseNotification({
                caseId,
                isUnsigned: true,
                isActive: false,
                isRead: false,
                userId: user?.userId,
              });
            }
          } else if (isActiveCase) {
            // if (enabledActiveCaseSound) {
            //   if (!isUserOnActiveCasePage) {
            //     playSoundOne();
            //   }
            // }
            if (isOnActiveCaseSoundPage) {
              playSoundOne();
            }
            setIsHasNewActiveCaseNotification(true);

            // Add case notification to store
            addCaseNotification({
              caseId,
              isUnsigned: false,
              isActive: true,
              isRead: false,
              userId: user?.userId,
            });
          }

          if (isHasGroup) {
            await notify(`Group: ${groupName} - ${customerName || 'Unknown'}`, content || 'Media Content', {
              data: { caseId, channel: channelName, senderType, url: redirectUrl },
            });
          } else if (isGroup) {
            await notify(`Group: ${customerName || 'Unknown'}`, content || 'Media Content', {
              data: { caseId, channel: channelName, senderType, url: redirectUrl },
            });
          } else {
            await notify(`${customerName || 'Unknown'}`, content || 'Media Content', {
              data: { caseId, channel: channelName, senderType, url: redirectUrl },
            });
          }
        }

        if (isUnassignedCase) {
          if (messageId && customerSender) {
            caseMessageCountStore.getState().actions.setCaseMessageCount(caseId, messageId);
          }
          /**
           * Because supervisor can see all cases, we need to handle the case as active case if the case has a team
           */
          if (isHasTeam && user?.role?.name === 'supervisor') {
            handleActiveCaseUpdate(payload);
            return;
          }
          handleUnassignedCaseUpdate(payload);
        } else if (isResolvedCase) {
          handleResolvedCaseUpdate(payload);
        } else {
          if (messageId && customerSender) {
            caseMessageCountStore.getState().actions.setCaseMessageCount(caseId, messageId);
          }
          handleActiveCaseUpdate(payload);
        }
      } finally {
        setIsLoadingFromWebsocket(false);
      }
    },
    [
      playSoundOne,
      playSoundTwo,
      isExecuteNotification,
      setIsHasNewActiveCaseNotification,
      setIsHasNewUnassignedCaseNotification,
      addCaseNotification,
      // enabledActiveCaseSound,
      // enabledUnsignedCaseSound,
      // isUserOnActiveCasePage,
      // isUserOnUnassignedCasePage,
      isOnActiveCaseSoundPage,
      isOnUnassignedCaseSoundPage,
      setIsLoadingFromWebsocket,
      updateGroupMessagesCache,
    ]
  );

  const updateCaseDetailsCache = useCallback((caseId: string, messageId: string, payload: TCase) => {
    queryClient.setQueryData(
      [GET_INFINITE_MESSAGES_QUERY_KEY, caseId, DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS],
      (old: any) => {
        if (!old?.pages?.[0]?.payload) return old;

        const firstPage = old.pages[0];
        const messageExists = firstPage.payload.some((msg: any) => msg.messageId === messageId);
        if (messageExists) return old;

        return {
          ...old,
          pages: [{ ...firstPage, payload: [...firstPage.payload, payload] }, ...old.pages.slice(1)],
        };
      }
    );
  }, []);

  const handleUnassignedCaseUpdate = useCallback(
    (payload: TCase) => {
      const { caseId, messageId } = payload;
      const listUnassignedCase = casesStore.getState().listUnassignCase;
      const existingCase = listUnassignedCase.find((item) => item.caseId === caseId);

      if (existingCase) {
        const updatedList = listUnassignedCase.map((item, _index) => (item.caseId === caseId ? payload : item));
        setListUnassignCase(updatedList);

        updateCaseDetailsCache(caseId, messageId, payload);

        if (payload?.groupInfoId) {
          updateGroupMessagesCache(payload?.groupInfoId, messageId, payload);
        }
      }
    },
    [setListUnassignCase, updateCaseDetailsCache, updateGroupMessagesCache]
  );

  const handleResolvedCaseUpdate = useCallback(
    (payload: TCase) => {
      const { caseId, messageId } = payload;
      const listResolvedCase = casesStore.getState().listResolvedCase;
      const existingCase = listResolvedCase.find((item) => item.caseId === caseId);

      if (existingCase) {
        // Create a new array with the updated case to ensure Zustand detects the change
        const updatedList = listResolvedCase.map((item, _index) => (item.caseId === caseId ? payload : item));
        setListResolvedCase(updatedList);
        updateCaseDetailsCache(caseId, messageId, payload);

        if (payload?.groupInfoId) {
          updateGroupMessagesCache(payload?.groupInfoId, messageId, payload);
        }
      }
    },
    [setListResolvedCase, updateCaseDetailsCache, updateGroupMessagesCache]
  );

  const handleActiveCaseUpdate = useCallback(
    (payload: TCase) => {
      const { caseId, messageId } = payload;
      const listActiveCase = casesStore.getState().listActiveCase;
      const selectedCase = casesStore.getState().selectedCase;
      const existingCase = listActiveCase.find((item) => item.caseId === caseId);

      if (existingCase) {
        // Create a new array with the updated case to ensure Zustand detects the change
        const updatedList = listActiveCase.map((item, _index) => (item.caseId === caseId ? payload : item));
        setListActiveCase(updatedList);

        if (selectedCase?.caseId === caseId) {
          setSelectedCase(payload);
        }

        // if (messageId) {
        //   caseMessageCountStore.getState().actions.setCaseMessageCount(caseId, messageId);
        // }

        updateCaseDetailsCache(caseId, messageId, payload);

        if (payload?.groupInfoId) {
          updateGroupMessagesCache(payload?.groupInfoId, messageId, payload);
        }

        updateOrSaveCaseUnread(caseId, payload.messageOrder);
        casesStore.getState().actions.bumpUnreadVersion();
      }
    },
    [setListActiveCase, setSelectedCase, updateCaseDetailsCache, updateGroupMessagesCache, updateOrSaveCaseUnread]
  );

  const handleMessageSentStatusUpdate = useCallback(
    (payload: TMessageId) => {
      try {
        const listActiveCase = casesStore.getState().listActiveCase;
        const isExist = listActiveCase.find((item) => item.messageId === payload);

        if (isExist) {
          const modifiedListActiveCase = listActiveCase.map((item) =>
            item.messageId === payload ? { ...item, isSent: true } : item
          );
          setListActiveCase(modifiedListActiveCase);

          queryClient.setQueryData(
            [GET_INFINITE_MESSAGES_QUERY_KEY, isExist.caseId, DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS],
            (old: any) => {
              if (!old) return;
              const firstPage = old.pages[0];
              const exists = firstPage.payload.some((msg: any) => msg.messageId === payload);
              if (!exists) return old;

              return {
                ...old,
                pages: [
                  {
                    ...firstPage,
                    payload: firstPage.payload.map((msg: any) =>
                      msg.messageId === payload ? { ...msg, isSent: true } : msg
                    ),
                  },
                  ...old.pages.slice(1),
                ],
              };
            }
          );
        }
      } finally {
        setIsLoadingFromWebsocket(false);
      }
    },
    [setListActiveCase, setIsLoadingFromWebsocket]
  );

  // EFFECTS
  // Reset loading state when socket disconnects to prevent stuck loading state
  // This ensures loading state is reset on disconnect/reconnect
  useEffect(() => {
    if (!socket) return;

    // When socket disconnects, reset loading state
    // This handles both initial disconnect and reconnection scenarios
    if (!socket.isConnected) {
      setIsLoadingFromWebsocket(true);
    }
  }, [socket?.isConnected, setIsLoadingFromWebsocket]);

  useEffect(() => {
    if (!socket?.isConnected || eventListenersAttached.current) return;

    socket.on('case_list', handleCaseListUpdate);
    socket.on('new_message', handleNewMessage);
    socket.on('message_sent_status', handleMessageSentStatusUpdate);

    eventListenersAttached.current = true;

    return () => {
      socket.off('case_list', handleCaseListUpdate);
      socket.off('new_message', handleNewMessage);
      socket.off('message_sent_status', handleMessageSentStatusUpdate);
      eventListenersAttached.current = false;
      // Reset loading state when listeners are removed (e.g., on unmount or disconnect)
      setIsLoadingFromWebsocket(false);
    };
  }, [socket, handleCaseListUpdate, handleNewMessage, handleMessageSentStatusUpdate, setIsLoadingFromWebsocket]);

  return null;
}
